import React from 'react';
import Button from 'components/Button';
import { CardElement } from 'react-stripe-elements';
import CoachingHeading from 'coaching/components/CoachingHeading';
import { CoachingReservation } from 'coaching/containers/GetAvailableReservationsContainer';
import CoachingSelfServeReservationDetails from 'coaching/components/CoachingSelfServeView/CoachingSelfServeReservationDetails';
import Divider from 'components/Divider';
import Copy from 'components/Copy';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import Label from 'components/Label';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import Text from 'components/Text';

type Props = {
  childName?: string;
  purchaseForConnectedChild: boolean;
  reservation?: CoachingReservation;
  userTimezone: string;
  onCardInputCompleted: () => void;
  disabledContinueButton: boolean;
  onSubmitPayment: () => void;
  submittingPayment: boolean;
};

export default function CoachingSelfServePaymentView(props: Props) {
  const eventPrefix = 'coaching.self_schedule_purchase.review';
  const trackEvent = (key: string) => {
    EventTracking.trackKey(`${eventPrefix}.${key}`);
  };

  React.useEffect(() => {
    trackEvent('view');
  }, []);

  const {
    childName,
    reservation,
    userTimezone,
    disabledContinueButton,
    onSubmitPayment,
    purchaseForConnectedChild,
    submittingPayment,
  } = props;

  if (!childName || !reservation) {
    return (
      <ErrorReporter error="CoachingSelfServePaymentView - tried to render without selecting a student or reservation">
        Oops - looks like we're missing some information. Please go back and complete all form
        fields.
      </ErrorReporter>
    );
  }
  return (
    <React.Fragment>
      <CoachingHeading size="medium">Confirm your free session</CoachingHeading>
      <CoachingSelfServeReservationDetails
        reservation={reservation}
        childName={childName}
        userTimezone={userTimezone}
      />
      <Divider size="medium" />
      <Row size="small">
        <Copy align="left" size="small">
          Please enter a payment method to verify your identity and to serve as parental consent.
          You won’t be charged unless you schedule another session.
        </Copy>
      </Row>
      <Row>
        <Label htmlFor="cardnumber">Credit or debit card</Label>
        <CardElement
          data-test="credit-card-input"
          className="CoachingIsolatedView-cardInput"
          onChange={(e: stripe.elements.ElementChangeResponse) => {
            if (e.complete && !e.error) {
              props.onCardInputCompleted();
            }
          }}
          onFocus={() => trackEvent('credit_card_input.click')}
        />
      </Row>
      <Row size="small">
        <Button
          data-test="payment-submit-button"
          eventKey={`${eventPrefix}.confirm.click`}
          color="koolaid"
          disabled={disabledContinueButton}
          onClick={onSubmitPayment}
          loading={submittingPayment}>
          Confirm session
        </Button>
      </Row>
      {renderLegalText(purchaseForConnectedChild, eventPrefix)}
    </React.Fragment>
  );

  function renderLegalText(purchaseForConnectedChild: boolean, eventPrefix: string) {
    if (purchaseForConnectedChild) {
      return (
        <Text size="xsmall" color="thunder">
          By clicking “Confirm session”, you consent for your child to use Remind for the purpose of
          Remind Coaching.
        </Text>
      );
    } else {
      return (
        <Text size="xsmall" color="thunder">
          By clicking “Confirm session”, you consent for your child to use Remind for the purpose of
          Remind Coaching. We collect their name, subject of interest, session audio/video, and
          computer session information for quality and safety purposes. Remind never sells or shares
          this information. View our{' '}
          <Link
            eventKey={`${eventPrefix}.privacy_policy.click`}
            href={LinkConstants.PRIVACY_POLICY}
            target="_blank"
            underline
            weight="semibold"
            color="ink">
            Privacy Policy.
          </Link>
        </Text>
      );
    }
  }
}
